import { useEffect, useId } from "react";
import {
  Box,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import {
  SourcingEventV2Dto,
  useListBidV2SForEventQuery,
} from "../../../autogen/bff-api";
import { AwardTab } from "./award/AwardTab";
import { BidsTab } from "./BidsTab";
import { CommunicationTab } from "./communication/CommunicationTab";
import { EvaluationTab } from "./evaluation/EvaluationTab";
import { RequestTab } from "./RequestTab";
import { QualificationTab } from "./qualification/QualificationTab";
import { MinimumRequirementsTab } from "./minimum-requirements/MinimumRequirementsTab";
import { DpsCompetitionsTab } from "./dps/DpsCompetitionsTab";
import { DpsMembersTab } from "./dps/DpsMembersTab";
import { isBidDeadlinePassed } from "./supplierRows";
import { visibleTabGroups, type EventTab } from "./eventTabDefinitions";
import { useEventTabLabel } from "./useEventTabLabel";
import { useRoundSelection } from "./rounds/RoundSelectionContext";

export type { EventTab } from "./eventTabDefinitions";
export { DEFAULT_TAB, parseEventTab } from "./eventTabDefinitions";

interface Props {
  event: SourcingEventV2Dto;
  activeTab: EventTab;
  normalizeTo: EventTab | null;
  onTabChange: (tab: EventTab) => void;
}

export const EventTabs = ({ event, activeTab, normalizeTo, onTabChange }: Props) => {
  const tabLabel = useEventTabLabel();
  const groupLabelId = useId();
  const { selectedRound } = useRoundSelection();
  const { data: bidsData } = useListBidV2SForEventQuery({
    eventId: event.id,
  });
  // The badge counts delivered bids, not suppliers — a number beside "Bids" is read as bids,
  // so invitations and interest registrations must not inflate it. Same reveal rule as BidsTab:
  // public bids stay hidden until the deadline, while a private buyer sees deliveries as they
  // arrive (the BFF is the real gate; this mirrors it client-side).
  const revealedBids =
    isBidDeadlinePassed(event) || event.eventContext.type === "Private" ? bidsData?.bids ?? [] : [];
  const deliveredBidCount = revealedBids.filter((bid) =>
    selectedRound ? bid.forNegotiationRound === selectedRound.id : bid.forNegotiationRound == null,
  ).length;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const schemeValidTo = publicContext?.dpsScheme?.validTo ?? publicContext?.qualificationScheme?.validTo;
  const schemeOpen =
    event.status === "PUBLISHED" && schemeValidTo != null && new Date(schemeValidTo) > new Date();
  const visibleGroups = visibleTabGroups(event);
  const tabs = visibleGroups.flatMap((group) => group.tabs);
  const index = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === activeTab),
  );
  useEffect(() => {
    if (normalizeTo != null) onTabChange(normalizeTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizeTo]);

  const panelFor = (key: EventTab) => {
    switch (key) {
      case "request":
        return <RequestTab event={event} />;
      case "qualification":
        return <QualificationTab event={event} />;
      case "minimumRequirements":
        return <MinimumRequirementsTab event={event} />;
      case "dpsSuppliers":
        return <DpsMembersTab event={event} schemeOpen={schemeOpen} />;
      case "dpsCompetitions":
        return <DpsCompetitionsTab event={event} />;
      case "bids":
        return <BidsTab event={event} />;
      case "evaluation":
        return <EvaluationTab event={event} />;
      case "award":
        return <AwardTab event={event} />;
      case "communication":
        return <CommunicationTab event={event} />;
    }
  };

  return (
    <Tabs
      index={index}
      onChange={(i) => onTabChange(tabs[i].key)}
      colorScheme="teal"
      isLazy
    >
      {/* The scroll lives on the wrapper, not the TabList: an overflow other than `visible` on
          the list clips the selected tab's 2px underline, which sits in the list's border box. */}
      <Box overflowX="auto">
        <TabList minW="max-content" alignItems="flex-end">
          {visibleGroups.map((group, groupIndex) => (
            // ARIA lets a tablist own only tabs, so the group heading stays hidden; accname § 4.1
            // still reads a hidden node that aria-describedby points at directly.
            <Box key={group.id} role="presentation" ml={groupIndex > 0 ? "6" : undefined}>
              <Text
                id={`${groupLabelId}-${group.id}`}
                aria-hidden="true"
                px="4"
                pb="2"
                fontSize="xs"
                fontWeight="bold"
                textTransform="uppercase"
                letterSpacing="wide"
                color="smMuted"
              >
                {tabLabel(group.labelKey)}
              </Text>
              <Flex
                role="presentation"
                borderLeft={groupIndex > 0 ? "1px solid" : undefined}
                borderColor="smBorder"
              >
                {group.tabs.map((tab) => (
                  <Tab
                    key={tab.key}
                    whiteSpace="nowrap"
                    aria-describedby={`${groupLabelId}-${group.id}`}
                  >
                    {tabLabel(tab.labelKey)}
                    {tab.key === "bids" && deliveredBidCount > 0 ? ` (${deliveredBidCount})` : ""}
                  </Tab>
                ))}
              </Flex>
            </Box>
          ))}
        </TabList>
      </Box>
      <TabPanels>
        {tabs.map((tab) => (
          <TabPanel key={tab.key} px="0">
            {panelFor(tab.key)}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};
